import { useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Users,
  UserPlus,
  RefreshCw,
  AlertTriangle,
  KeyRound,
  Pencil,
  Ban,
  Download,
  Search,
  ShieldCheck,
  ChevronDown,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { useT, useLang } from '@/i18n';
import { exportCsv, csvFilename } from '@/lib/csv';
import { toast } from '@/stores/toast.store';
import { useUsersList, useUpdateUser } from '@/features/users/useUsers';
import { EmptyState, ErrorState, Skeleton } from '@/components/chassis/States';
import { Pager } from '@/components/chassis/Pager';
import { UserModal } from './components/UserModal';
import { ResetPasswordModal } from './components/ResetPasswordModal';
import type { AdminUser, UserRole } from '@/types/api';

/**
 * F5-T6a · Admin 04 — Centro de equipo (contrato 2026-08-28).
 *
 * Fusiona equipo general + equipo de uploaders + actividad de uploaders en
 * UNA tabla con filtro por rol (contador en cada pestaña) y columna de
 * actividad. Alta, edición y desactivación de los cinco roles en una sola
 * superficie. NOTA de fusión: las rutas viejas /admin/uploader-team y
 * /admin/uploaders-activity siguen ruteadas (AdminShell/AdminChrome están
 * fuera del alcance T6a) — apagarlas viaja con el arco del sidebar.
 *
 * MUEREN (contrato §dies): el color por rol (un solo tinte teal) · el
 * borrado de usuarios (se desactiva, no se borra — HIPAA) · el confirm()
 * del navegador (diálogo propio con las consecuencias dichas antes) · el
 * «último login» como métrica de productividad (queda como dato de
 * seguridad; la productividad vive en la ventana 3).
 *
 * NECESITA-SERVER (no se finge nada de esto):
 * - «Actividad de hoy» por persona y rol: ningún endpoint admin la trae en
 *   la lista de usuarios → la columna muestra guion.
 * - Alta sin contraseña con invitación de un solo uso: POST /admin/users
 *   EXIGE initialPassword → se conserva el UserModal vivo (contraseña
 *   generada + copy out-of-band) como camino funcional.
 * - «Reenviar invitación» del estado Invitación pendiente: no hay endpoint
 *   de invitaciones → la acción no se dibuja (dibujar un botón muerto sería
 *   fingir).
 * - Revocación de sesiones al desactivar/resetear: el copy del diálogo es
 *   el del contrato; si el server no revoca sesiones vivas al PATCH
 *   active=false, el bullet 1 promete de más — verificar en el arco server.
 *
 * DESVIACIÓN DECLARADA: «Invitación pendiente» se mapea al estado real
 * mustChangePassword (contraseña temporal sin estrenar) — es el análogo
 * honesto del sistema de hoy, no una invitación de verdad.
 */

const ROLE_SEQ: UserRole[] = ['agent', 'navigator', 'uploader', 'supervisor', 'admin'];

const ROLE_FILTER_KEY = {
  all: 'f5t6a.tc.filter.all',
  agent: 'f5t6a.tc.filter.agent',
  navigator: 'f5t6a.tc.filter.navigator',
  uploader: 'f5t6a.tc.filter.uploader',
  supervisor: 'f5t6a.tc.filter.supervisor',
  admin: 'f5t6a.tc.filter.admin',
} as const;

const ROLE_LABEL_KEY = {
  agent: 'admin.team.edit.role.agent',
  navigator: 'admin.team.edit.role.navigator',
  uploader: 'admin.team.edit.role.uploader',
  supervisor: 'admin.team.edit.role.supervisor',
  admin: 'admin.team.edit.role.admin',
} as const;

const ROLE_SINGULAR_KEY = {
  agent: 'f5t6a.tc.role1.agent',
  navigator: 'f5t6a.tc.role1.navigator',
  uploader: 'f5t6a.tc.role1.uploader',
  supervisor: 'f5t6a.tc.role1.supervisor',
  admin: 'f5t6a.tc.role1.admin',
} as const;

type UserStatus = 'active' | 'invited' | 'off';

function statusOf(u: AdminUser): UserStatus {
  if (!u.active) return 'off';
  if (u.mustChangePassword && !u.lastLoginAt) return 'invited';
  return 'active';
}

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('');
}

const PAGE_SIZE = 25;

export function TeamPage(): JSX.Element {
  const t = useT();
  const lang = useLang();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filtro de rol en ?role= (conserva los deep-links viejos del sidebar).
  const roleParam = searchParams.get('role');
  const roleFilter: UserRole | 'all' =
    roleParam && (ROLE_SEQ as string[]).includes(roleParam) ? (roleParam as UserRole) : 'all';
  const setRoleFilter = (next: UserRole | 'all'): void => {
    const p = new URLSearchParams(searchParams);
    if (next === 'all') p.delete('role');
    else p.set('role', next);
    setSearchParams(p, { replace: true });
    setPage(0);
  };

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AdminUser | null>(null);
  const [resetTarget, setResetTarget] = useState<AdminUser | null>(null);
  const [dialog, setDialog] = useState<{ kind: 'deactivate' | 'reset'; user: AdminUser } | null>(null);
  const [liveMsg, setLiveMsg] = useState('');
  const [scopePendingId, setScopePendingId] = useState<string | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  // El equipo se cuenta en decenas, no en miles: una sola carga con tope 500
  // y filtro/paginado client-side (los contadores por rol necesitan el
  // universo completo). Si algún día total > 500, el pie lo delata.
  const { data, isLoading, error, isFetching, refetch } = useUsersList({}, 500, 0);
  const updateMut = useUpdateUser();

  const all = useMemo(() => data?.items ?? [], [data]);

  const countsByRole = useMemo(() => {
    const c = new Map<UserRole | 'all', number>([['all', all.length]]);
    for (const r of ROLE_SEQ) c.set(r, 0);
    for (const u of all) c.set(u.role, (c.get(u.role) ?? 0) + 1);
    return c;
  }, [all]);

  const visible = useMemo(() => {
    let out = all;
    if (roleFilter !== 'all') out = out.filter((u) => u.role === roleFilter);
    // CONSERVA (E-23): la búsqueda por nombre/correo de la pantalla vieja.
    const q = search.trim().toLowerCase();
    if (q) out = out.filter((u) => u.fullName.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
    // Los desactivados NO se esconden (contrato) — van al final, visibles.
    return [...out].sort((a, b) => Number(!a.active) - Number(!b.active) || a.fullName.localeCompare(b.fullName));
  }, [all, roleFilter, search]);

  const pages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const pageClamped = Math.min(page, pages - 1);
  const pageRows = visible.slice(pageClamped * PAGE_SIZE, (pageClamped + 1) * PAGE_SIZE);

  const fmtLast = (iso: string | null): string => {
    if (!iso) return t('f5t6a.tc.never');
    const d = new Date(iso);
    const now = new Date();
    const hm = d.toLocaleTimeString(lang === 'es' ? 'es' : 'en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
    if (d.toDateString() === now.toDateString()) return t('f5t6a.tc.today', { t: hm });
    return d.toLocaleDateString(lang === 'es' ? 'es' : 'en', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const handleExport = (): void => {
    // Columnas cerradas del contrato: nombre, correo, rol, estado y último
    // ingreso — sin contraseñas, hashes, tokens ni cifras de plata. Respeta
    // el filtro de rol vigente. NECESITA-SERVER: la auditoría del export
    // (quién/cuándo/con qué filtro) — este CSV es client-side.
    exportCsv<AdminUser>({
      filename: csvFilename('team'),
      columns: [
        { header: t('f5t6a.tc.col.person'), accessor: (u) => u.fullName },
        { header: 'Email', accessor: (u) => u.email },
        { header: t('f5t6a.tc.col.role'), accessor: (u) => t(ROLE_LABEL_KEY[u.role]) },
        {
          header: t('f5t6a.tc.col.status'),
          accessor: (u) => {
            const s = statusOf(u);
            return t(s === 'active' ? 'f5t6a.tc.status.active' : s === 'invited' ? 'f5t6a.tc.status.invited' : 'f5t6a.tc.status.off');
          },
        },
        { header: t('f5t6a.tc.col.lastLogin'), accessor: (u) => u.lastLoginAt ?? '' },
      ],
      rows: visible,
    });
  };

  const openDialog = (kind: 'deactivate' | 'reset', user: AdminUser, trigger: HTMLElement | null): void => {
    returnFocusRef.current = trigger;
    setDialog({ kind, user });
  };
  const closeDialog = (): void => {
    setDialog(null);
    returnFocusRef.current?.focus();
    returnFocusRef.current = null;
  };

  const confirmDialog = (): void => {
    if (!dialog) return;
    const { kind, user } = dialog;
    if (kind === 'deactivate') {
      updateMut.mutate(
        { id: user.id, input: { active: false } },
        {
          onSuccess: () => {
            const msg = t('f5t6a.tc.doneDeact', { name: user.fullName });
            setLiveMsg(msg);
            toast.success(t('f5t6a.tc.act.deactivate'), msg);
            closeDialog();
          },
        },
      );
    } else {
      // El camino real del reseteo sigue siendo la contraseña temporal del
      // ResetPasswordModal (el endpoint exige newPassword) — el diálogo del
      // contrato dice las consecuencias ANTES; ver NECESITA-SERVER arriba.
      closeDialog();
      setResetTarget(user);
    }
  };

  const reactivate = (user: AdminUser): void => {
    updateMut.mutate(
      { id: user.id, input: { active: true } },
      {
        onSuccess: () => {
          const msg = t('f5t6a.tc.doneReact', { name: user.fullName });
          setLiveMsg(msg);
          toast.success(t('f5t6a.tc.act.reactivate'), msg);
        },
      },
    );
  };

  // El pill de alcance en la fila cicla full ↔ closer (para no abrir la
  // edición completa por un cambio de alcance). Mientras el pool D-1…D-7 no
  // gobierne el motor, escribe la etiqueta real (navigator_scope, 076): el
  // día que se firme, esa misma etiqueta filtra la asignación. El scope
  // 'pool' (el buzón, B2) NO se cicla desde acá — se muestra de solo lectura.
  const cycleScope = (user: AdminUser): void => {
    const next = user.navigatorScope === 'closer' ? 'full' : 'closer';
    setScopePendingId(user.id);
    updateMut.mutate(
      { id: user.id, input: { navigatorScope: next } },
      { onSettled: () => setScopePendingId(null) },
    );
  };

  const filterLabel = (r: UserRole | 'all'): string => t(ROLE_FILTER_KEY[r]);

  const footLabel =
    visible.length === 1
      ? t('f5t6a.foot.person', { p: pageClamped + 1, pp: pages })
      : t('f5t6a.foot.people', { n: visible.length, p: pageClamped + 1, pp: pages });

  return (
    <div className="max-w-[1440px] mx-auto font-body text-nav-navy animate-fade-up-slow motion-reduce:animate-none">
      {/* Resultado de acciones, anunciado (a11y del contrato). */}
      <span aria-live="polite" className="sr-only">{liveMsg}</span>

      {/* ---- Barra superior: cápsula de roles + acciones ---- */}
      <div className="flex items-center gap-3.5 flex-wrap mb-[22px]">
        <div
          className="inline-flex flex-wrap bg-white border-[1.5px] border-nav-card rounded-[10px] p-[3px] gap-[2px]"
          role="group"
          aria-label={t('f5t6a.tc.filterAria')}
        >
          {(['all', ...ROLE_SEQ] as Array<UserRole | 'all'>).map((r) => {
            const on = roleFilter === r;
            return (
              <button
                key={r}
                type="button"
                aria-pressed={on}
                onClick={() => setRoleFilter(r)}
                className={cn(
                  'inline-flex items-center gap-1.5 min-h-[44px] px-[15px] rounded-lg font-head text-[13px] font-bold transition-colors duration-150 motion-reduce:transition-none',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2',
                  on ? 'bg-nav-navy text-white hover:bg-nav-navyHover' : 'text-nav-slate hover:bg-nav-soft hover:text-nav-navy',
                )}
              >
                {filterLabel(r)}
                <span className={cn('tabular-nums font-semibold', on ? 'text-white/70' : 'text-nav-faint')}>
                  {countsByRole.get(r) ?? 0}
                </span>
              </button>
            );
          })}
        </div>

        <div className="ml-auto flex items-center gap-2.5 flex-wrap">
          {/* CONSERVA (E-23): búsqueda + refresh de la pantalla vieja. */}
          <span className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-nav-faint" aria-hidden />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              placeholder={t('f5t6a.tc.searchPh')}
              aria-label={t('f5t6a.tc.searchPh')}
              className="min-h-[44px] pl-8 pr-3 rounded-lg border-[1.5px] border-nav-card bg-white text-[13px] text-nav-navy placeholder:text-nav-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2 w-[190px]"
            />
          </span>
          <button
            type="button"
            onClick={() => void refetch()}
            disabled={isFetching}
            aria-label={t('common.refresh')}
            title={t('common.refresh')}
            className="inline-flex items-center justify-center min-w-[44px] min-h-[44px] rounded-lg text-nav-slate hover:bg-nav-hair hover:text-nav-navy transition-colors duration-150 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2 disabled:opacity-40"
          >
            <RefreshCw className="w-4 h-4" aria-hidden />
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={visible.length === 0}
            className="inline-flex items-center gap-1.5 min-h-[44px] px-4 rounded-lg border-[1.5px] border-nav-line bg-white font-head text-[13px] font-bold text-nav-navy hover:bg-nav-tealTint hover:border-nav-teal hover:text-nav-tealDark transition-colors duration-150 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2 disabled:opacity-40"
          >
            <Download className="w-4 h-4" aria-hidden />
            {t('f5t6a.tc.export')}
          </button>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-1.5 min-h-[44px] px-[18px] rounded-lg bg-nav-navy font-head text-[13.5px] font-bold text-white hover:bg-nav-navyHover transition-colors duration-150 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2"
          >
            <UserPlus className="w-4 h-4" aria-hidden />
            {t('f5t6a.tc.new')}
          </button>
        </div>
      </div>

      {/* ---- La tabla ---- */}
      {isLoading ? (
        <div className="bg-white border-[1.5px] border-nav-card rounded-xl p-6 space-y-2.5" aria-busy="true">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-[64px] rounded-lg" />
          ))}
        </div>
      ) : error ? (
        <div className="bg-white border-[1.5px] border-nav-card rounded-xl">
          <ErrorState
            icon={<AlertTriangle className="w-6 h-6" aria-hidden />}
            title={t('f5t6a.tc.errTitle')}
            body={t('f5t6a.tc.errBody')}
            action={{ label: t('f5t6a.lm.retry'), onClick: () => void refetch() }}
          />
        </div>
      ) : visible.length === 0 ? (
        <div className="bg-white border-[1.5px] border-nav-card rounded-xl">
          <EmptyState
            icon={<Users className="w-6 h-6" aria-hidden />}
            title={
              roleFilter === 'all'
                ? t('f5t6a.tc.emptyAll')
                : t('f5t6a.tc.emptyRole', { role: t(ROLE_SINGULAR_KEY[roleFilter]) })
            }
            body={t('f5t6a.tc.emptyBody')}
            action={{ label: t('f5t6a.tc.new'), onClick: () => setCreateOpen(true) }}
          />
        </div>
      ) : (
        <div className="bg-white border-[1.5px] border-nav-card rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  {[
                    t('f5t6a.tc.col.person'),
                    t('f5t6a.tc.col.role'),
                    t('f5t6a.tc.col.status'),
                    t('f5t6a.tc.col.lastLogin'),
                    t('f5t6a.tc.col.activity'),
                  ].map((h, i) => (
                    <th
                      key={h}
                      className={cn(
                        'h-[38px] px-[18px] text-left font-head text-[10.5px] font-bold tracking-[0.1em] uppercase text-nav-slate bg-nav-softer border-y border-nav-hair whitespace-nowrap',
                        i === 0 && 'pl-6',
                      )}
                    >
                      {h}
                    </th>
                  ))}
                  <th className="h-[38px] px-[18px] text-right font-head text-[10.5px] font-bold tracking-[0.1em] uppercase text-nav-slate bg-nav-softer border-y border-nav-hair whitespace-nowrap">
                    {t('f5t6a.tc.col.actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((u, i) => {
                  const status = statusOf(u);
                  const off = status === 'off';
                  return (
                    <tr
                      key={u.id}
                      style={{ animationDelay: `${i * 0.05}s` }}
                      className={cn(
                        'animate-fade-up motion-reduce:animate-none transition-colors duration-150 motion-reduce:transition-none',
                        // Desactivada: visible en gris, nunca escondida.
                        off ? 'bg-nav-softer opacity-70 hover:opacity-90' : 'hover:bg-[#f4fafa]',
                      )}
                    >
                      {/* Persona: avatar + nombre + correo (identidad en dos líneas) */}
                      <td className="pl-6 pr-[18px] h-[64px] border-t border-nav-hair">
                        <span className="flex items-center gap-[11px] min-w-0">
                          <span
                            aria-hidden
                            className={cn(
                              'w-[34px] h-[34px] rounded-full text-white font-head text-[12px] font-bold flex items-center justify-center flex-none',
                              off ? 'bg-nav-faint' : 'bg-nav-navy',
                            )}
                          >
                            {initials(u.fullName || u.email)}
                          </span>
                          <span className="min-w-0">
                            <span className="flex items-center gap-1.5 font-head text-[13.5px] font-bold truncate">
                              {u.fullName}
                              {/* CONSERVA: el dato MFA vivía como columna; el
                                  catálogo del contrato la cierra — queda como
                                  escudo con tooltip junto al nombre. */}
                              {u.mfaEnabled && (
                                <span title={t('f5t6a.tc.mfaOn')} className="inline-flex flex-none">
                                  <ShieldCheck className="w-3.5 h-3.5 text-nav-tealDark" aria-label={t('f5t6a.tc.mfaOn')} />
                                </span>
                              )}
                            </span>
                            <span className="block text-[12px] text-nav-slate truncate">{u.email}</span>
                          </span>
                        </span>
                      </td>

                      {/* Rol: UN solo tinte para los 5 (el semáforo por rol muere) */}
                      <td className="px-[18px] h-[64px] border-t border-nav-hair whitespace-nowrap">
                        <span className="inline-flex items-center rounded-full bg-nav-tealTint text-nav-tealDark font-head text-[11.5px] font-bold px-2.5 py-[3px]">
                          {t(ROLE_LABEL_KEY[u.role])}
                        </span>
                        {u.role === 'navigator' && (
                          u.navigatorScope === 'pool' ? (
                            <span
                              title={t('f5t6a.tc.scope.title')}
                              className="mt-[5px] flex w-fit items-center gap-[5px] rounded-full border border-[#c9e2e2] bg-[#f4fafa] px-[9px] min-h-[28px] font-body text-[11.5px] font-semibold text-nav-tealDark"
                            >
                              {t('f5t6a.tc.scope.pool')}
                            </span>
                          ) : (
                            <button
                              type="button"
                              title={t('f5t6a.tc.scope.title')}
                              disabled={scopePendingId === u.id}
                              onClick={() => cycleScope(u)}
                              className="mt-[5px] flex items-center gap-[5px] rounded-full border border-[#c9e2e2] bg-[#f4fafa] px-[9px] min-h-[28px] font-body text-[11.5px] font-semibold text-nav-tealDark hover:bg-nav-tealTint hover:border-nav-teal transition-colors duration-150 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-1 disabled:opacity-50"
                            >
                              {t(u.navigatorScope === 'closer' ? 'f5t6a.tc.scope.closer' : 'f5t6a.tc.scope.full')}
                              <ChevronDown className="w-[13px] h-[13px]" aria-hidden />
                            </button>
                          )
                        )}
                      </td>

                      {/* Estado: punto + etiqueta de texto, nunca solo color */}
                      <td className="px-[18px] h-[64px] border-t border-nav-hair whitespace-nowrap">
                        <span className="inline-flex items-center gap-2 text-[13px] font-semibold text-nav-slate">
                          <span
                            aria-hidden
                            className={cn(
                              'w-2 h-2 rounded-full flex-none',
                              status === 'active' && 'bg-nav-sage',
                              status === 'invited' && 'bg-nav-amber',
                              status === 'off' && 'bg-nav-line',
                            )}
                          />
                          {t(status === 'active' ? 'f5t6a.tc.status.active' : status === 'invited' ? 'f5t6a.tc.status.invited' : 'f5t6a.tc.status.off')}
                        </span>
                      </td>

                      {/* Último ingreso: dato de seguridad, no de productividad */}
                      <td className="px-[18px] h-[64px] border-t border-nav-hair text-[13.5px] text-nav-slate tabular-nums whitespace-nowrap">
                        {fmtLast(u.lastLoginAt)}
                      </td>

                      {/* NECESITA-SERVER: «Actividad de hoy» por persona (los
                          cinco roles) no viaja en /admin/users — guion honesto
                          hasta que exista. */}
                      <td className="px-[18px] h-[64px] border-t border-nav-hair text-[13.5px] text-nav-slate tabular-nums whitespace-nowrap">
                        —
                      </td>

                      {/* Acciones */}
                      <td className="px-[18px] h-[64px] border-t border-nav-hair text-right whitespace-nowrap">
                        {off ? (
                          // Desactivada: UNA sola acción (contrato).
                          <button
                            type="button"
                            onClick={() => reactivate(u)}
                            disabled={updateMut.isPending}
                            className="inline-flex items-center min-h-[44px] px-3.5 rounded-lg font-head text-[13px] font-bold text-nav-tealDark hover:bg-nav-tealTint transition-colors duration-150 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2 disabled:opacity-40"
                          >
                            {t('f5t6a.tc.act.reactivate')}
                          </button>
                        ) : (
                          <span className="inline-flex items-center gap-[2px]">
                            <IconAction label={t('f5t6a.tc.act.edit')} onClick={() => setEditTarget(u)}>
                              <Pencil className="w-4 h-4" aria-hidden />
                            </IconAction>
                            <IconAction label={t('f5t6a.tc.act.reset')} onClick={(e) => openDialog('reset', u, e.currentTarget)}>
                              <KeyRound className="w-4 h-4" aria-hidden />
                            </IconAction>
                            <IconAction
                              label={t('f5t6a.tc.act.deactivate')}
                              destructive
                              onClick={(e) => openDialog('deactivate', u, e.currentTarget)}
                            >
                              <Ban className="w-4 h-4" aria-hidden />
                            </IconAction>
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pie: conteo + la nota de la fusión */}
          <div className="flex items-center justify-between gap-3 flex-wrap px-6 py-4 border-t border-nav-hair">
            <span className="text-[13px] text-nav-slate tabular-nums">{footLabel}</span>
            <span className="text-[13px] text-nav-slate">{t('f5t6a.tc.footNote')}</span>
          </div>
          <Pager
            page={pageClamped}
            pages={pages}
            onPage={setPage}
            prevLabel={t('f5t6a.pager.prev')}
            nextLabel={t('f5t6a.pager.next')}
            pageLabel={t('f5t6a.pager.page', { p: pageClamped + 1, pp: pages })}
            className="px-6 pb-4"
          />
        </div>
      )}

      {/* Diálogo propio del sistema — nunca el confirm() del navegador. */}
      {dialog && (
        <ConfirmDialog
          kind={dialog.kind}
          user={dialog.user}
          busy={updateMut.isPending}
          onCancel={closeDialog}
          onConfirm={confirmDialog}
        />
      )}

      {/* CONSERVA: UserModal (alta/edición con override de meta, alcance,
          idiomas y guardas de auto-edición) y ResetPasswordModal — son el
          camino funcional contra la API real; ver NECESITA-SERVER arriba. */}
      <UserModal open={createOpen} user={null} onClose={() => setCreateOpen(false)} />
      <UserModal open={!!editTarget} user={editTarget} onClose={() => setEditTarget(null)} />
      <ResetPasswordModal open={!!resetTarget} user={resetTarget} onClose={() => setResetTarget(null)} />
    </div>
  );
}

function IconAction({
  label,
  destructive,
  onClick,
  children,
}: {
  label: string;
  destructive?: boolean;
  onClick: (e: ReactMouseEvent<HTMLButtonElement>) => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className={cn(
        'inline-flex items-center justify-center w-[44px] h-[44px] rounded-lg transition-colors duration-150 motion-reduce:transition-none',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2',
        destructive
          // Rojo idle #b0574c del dibujo (sin token — literal declarado).
          ? 'text-[#b0574c] hover:bg-nav-redTint hover:text-nav-red'
          : 'text-nav-slate hover:bg-nav-hair hover:text-nav-navy',
      )}
    >
      {children}
    </button>
  );
}

/**
 * Diálogo compartido por Desactivar y Resetear: mismo velo, misma caja roja
 * de consecuencias (3 bullets, el tercero cambia según la acción). Foco
 * inicial en «Cancelar» — nunca en el botón destructivo; Escape cierra; el
 * foco vuelve a la fila de origen (lo maneja el caller).
 */
function ConfirmDialog({
  kind,
  user,
  busy,
  onCancel,
  onConfirm,
}: {
  kind: 'deactivate' | 'reset';
  user: AdminUser;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}): JSX.Element {
  const t = useT();
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Tab') {
        // Trampa de foco mínima entre los focusables del diálogo.
        const els = boxRef.current?.querySelectorAll<HTMLElement>('button:not([disabled])');
        if (!els || els.length === 0) return;
        const first = els[0];
        const last = els[els.length - 1];
        if (!first || !last) return;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const title = t(kind === 'deactivate' ? 'f5t6a.tc.deact.title' : 'f5t6a.tc.reset.title', { name: user.fullName });
  const body = t(kind === 'deactivate' ? 'f5t6a.tc.deact.body' : 'f5t6a.tc.reset.body');
  const note = t(kind === 'deactivate' ? 'f5t6a.tc.deact.note' : 'f5t6a.tc.reset.note');
  const confirmLabel = t(kind === 'deactivate' ? 'f5t6a.tc.deact.confirm' : 'f5t6a.tc.reset.confirm');
  const bullet3 = t(kind === 'deactivate' ? 'f5t6a.tc.bullet3Deact' : 'f5t6a.tc.bullet3Reset');

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-[rgba(18,43,68,.42)]"
      onClick={onCancel}
      role="presentation"
    >
      <div
        ref={boxRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tc-confirm-title"
        onClick={(e) => e.stopPropagation()}
        className="relative mx-auto mt-16 w-[520px] max-w-[calc(100%-64px)] rounded-[14px] border-[1.5px] border-nav-card bg-white shadow-[0_24px_60px_rgba(18,43,68,.28)] px-7 pt-[26px] pb-6 animate-fade-up motion-reduce:animate-none font-body text-nav-navy"
      >
        <div className="flex items-center gap-3.5">
          <span aria-hidden className="w-10 h-10 rounded-full bg-nav-redTint text-nav-red flex items-center justify-center flex-none">
            <AlertTriangle className="w-5 h-5" strokeWidth={2} aria-hidden />
          </span>
          <h2 id="tc-confirm-title" className="m-0 font-head text-[19px] font-bold leading-snug">{title}</h2>
        </div>

        <p className="mt-3.5 mb-0 text-[14.5px] leading-relaxed">{body}</p>

        {/* La caja roja: TRES consecuencias, dichas ANTES de confirmar. */}
        <div className="mt-3.5 rounded-xl border-[1.5px] border-[#e3b5ae] bg-nav-redTint px-[18px] py-4 text-nav-red">
          <p className="m-0 font-head text-[10.5px] font-bold tracking-[0.1em] uppercase">{t('f5t6a.tc.boxTitle')}</p>
          <ul className="mt-2 mb-0 pl-4 space-y-1.5 text-[13.5px] leading-relaxed list-disc">
            <li>{t('f5t6a.tc.bullet1')}</li>
            <li>{t('f5t6a.tc.bullet2')}</li>
            <li>{bullet3}</li>
          </ul>
        </div>

        <p className="mt-3.5 mb-0 text-[13.5px] leading-relaxed text-nav-slate">{note}</p>

        <div className="mt-5 flex justify-end gap-2.5">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            className="min-h-[44px] px-4 rounded-lg font-head text-[13.5px] font-bold text-nav-slate hover:bg-nav-hair hover:text-nav-navy transition-colors duration-150 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2"
          >
            {t('f5t6a.tc.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="min-h-[44px] px-[18px] rounded-lg bg-nav-red font-head text-[13.5px] font-bold text-white hover:bg-[#7a241b] transition-colors duration-150 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2 disabled:opacity-50"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
